// Every React Query key the app uses, in one place. React Query matches
// invalidate/cancel/getQueriesData by PREFIX, so a broader key covers every
// narrower one under it:
//
//   rfqs.all          ['rfqs']                    every RFQ list (all tabs, all searches)
//   rfqs.tab(t)       ['rfqs', t]                 one tab's lists, any search
//   rfqs.list(t, s)   ['rfqs', t, s]              the list RFQList renders
//
// Keep each key's array exactly as it was when it was a literal: changing a
// segment changes which cached queries an invalidation reaches.
// `queryKeys.test.ts` pins every key's shape.

export const queryKeys = {
  rfqs: {
    all: ["rfqs"] as const,
    tab: (tab: string) => ["rfqs", tab] as const,
    list: (tab: string, search: string) => ["rfqs", tab, search] as const,
  },
  rfqCounts: {
    all: ["rfq-counts"] as const,
    /** The tab counts; DIY counts only this user's RFQs. */
    forUser: (userName: string | undefined) => ["rfq-counts", userName] as const,
  },
  /**
   * Invalidated after a send and a PDF regeneration, but no query is cached
   * under it today, so those invalidations reach nothing. Kept so the calls
   * stay identical; drop it together with them.
   */
  rfq: (rfqId: number) => ["rfq", rfqId] as const,
  rfqDuplicate: (rfqId: number) => ["rfq-duplicate", rfqId] as const,
  attachmentCount: (rfqId: number) => ["attachment-count", rfqId] as const,
  failedRfqEmails: ["failedRfqEmails"] as const,
  blacklistedSenders: {
    all: ["blacklistedSenders"] as const,
    list: (showInactive: boolean) => ["blacklistedSenders", showInactive] as const,
  },
  /** One entry per filter set, shared by the Quote History page and the RFQ list's sidebar. */
  quoteHistory: (
    customerEmail: string,
    rfqId: number | "",
    startDate: Date | null,
    endDate: Date | null,
    keyword: string,
  ) => ["quoteHistory", customerEmail, rfqId, startDate, endDate, keyword] as const,
  inkPrices: {
    all: ["inkPrices"] as const,
    list: (page: number, rowsPerPage: number, search: string, brand: string) =>
      ["inkPrices", page, rowsPerPage, search, brand] as const,
  },
  genericItems: {
    all: ["genericItems"] as const,
    list: (page: number, rowsPerPage: number, search: string, category: string) =>
      ["genericItems", page, rowsPerPage, search, category] as const,
  },
  genericCategories: ["genericCategories"] as const,
  curatedPrices: {
    category: (category: string) => ["curatedPrices", category] as const,
    list: (category: string, page: number, rowsPerPage: number, search: string) =>
      ["curatedPrices", category, page, rowsPerPage, search] as const,
  },
};
